import { useState } from "react";
import { useReportsApproved } from "../../../hooks/useDashboardQueries";
import {
	DashboardReportType,
	ReportsApprovedResponse,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import DonutChart from "../charts/DonutChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatCount,
	formatOwnSplit,
	selectableYears,
	yearOptions,
} from "../dashboardConfig";
import { masterTenantName, TenantWidgetProps } from "../tenantChoice";
import { useExpandedView, useTenantChoice } from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import {
	APPROVED_TITLE,
	approvedEmptyText,
	approvedTypes,
	hasNoApproved,
	sumTypes,
} from "./reportsApproved";
import ReportsApprovedExpanded from "./ReportsApprovedExpanded";

interface ApprovedDonutProps {
	data: ReportsApprovedResponse;
	onlyTypes?: DashboardReportType[];
	ownLabel: string;
	context: string;
}

function ApprovedDonut({
	data,
	onlyTypes,
	ownLabel,
	context,
}: ApprovedDonutProps) {
	const types = approvedTypes(data, onlyTypes);
	const slices = types.map((type) => ({
		id: type.key,
		label: type.label,
		value: data.byType[type.key] ?? 0,
		color: type.color,
	}));
	const keys = types.map((type) => type.key);
	const total = sumTypes(data.byType, keys);
	const { own } = data;

	return (
		<DonutChart
			slices={slices}
			total={total}
			sliceDetail={
				own
					? (id) =>
							formatOwnSplit(
								ownLabel,
								own.byType[id as DashboardReportType] ?? 0,
								data.byType[id as DashboardReportType] ?? 0
							)
					: undefined
			}
			summary={`${APPROVED_TITLE}, ${context}: ${slices
				.map((slice) => `${slice.label} ${slice.value}`)
				.join(", ")}; ${total} in total.`}
			aside={
				<ul className="dashboard-legend">
					{own && (
						<li>
							{ownLabel} {formatCount(sumTypes(own.byType, keys))} of{" "}
							{formatCount(total)}
						</li>
					)}
					{slices.map((slice) => (
						<li key={slice.id} className="dashboard-legend__item">
							<span
								className="dashboard-legend__dot"
								style={{ background: slice.color }}
							/>
							{slice.label}
						</li>
					))}
				</ul>
			}
		/>
	);
}

/** Approved reports in the selected year, by type. */
function ReportsApprovedDonutWidget({
	tenants,
	currentTenantKey,
}: TenantWidgetProps) {
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"master",
		currentTenantKey
	);
	const [year, setYear] = useState(() => selectableYears()[0]);
	const expanded = useExpandedView();
	const query = useReportsApproved(target, year);
	const ownLabel = masterTenantName(tenants);

	return (
		<>
			<DashboardCard
				badge={BADGES.reports}
				title={APPROVED_TITLE}
				className="dashboard-widget--approved-donut dashboard-widget--tall"
				controls={
					<>
						<WidgetSelect
							value={tenant}
							options={choice.options}
							onChange={setTenant}
							ariaLabel={`Tenant for ${APPROVED_TITLE}`}
						/>
						<WidgetSelect
							value={year}
							options={yearOptions()}
							onChange={setYear}
							ariaLabel={`Year for ${APPROVED_TITLE}`}
						/>
					</>
				}
				onExpand={expanded.show}
				status={cardStatus(query, (data) => hasNoApproved(data))}
				emptyText={approvedEmptyText(year)}
				forbiddenText={`You don't have access to Reporting for ${label}.`}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<ApprovedDonut
						data={query.data}
						ownLabel={ownLabel}
						context={`${label} · ${year}`}
					/>
				)}
			</DashboardCard>
			<ReportsApprovedExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				title={APPROVED_TITLE}
				choice={choice}
				initialTenant={tenant}
				initialYear={year}
				renderChart={(data, onlyTypes, context) => (
					<ApprovedDonut
						data={data}
						onlyTypes={onlyTypes}
						ownLabel={ownLabel}
						context={context}
					/>
				)}
			/>
		</>
	);
}

export default ReportsApprovedDonutWidget;
